import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { SecureAuthService } from '../services/auth';
import { SecureApiService } from '../services/api';
import { useAuthStore } from '../stores/authStore';

/**
 * Interface para el contexto de autenticación
 */
interface AuthContextType {
    authService: SecureAuthService;
    apiService: SecureApiService;
    isInitialized: boolean;
}

/**
 * Props para el proveedor de autenticación
 */
interface AuthProviderProps {
    children: ReactNode;
    msalInstance: PublicClientApplication;
    apiBaseUrl?: string;
    defaultScopes?: string[];
}

// Crear el contexto
const AuthContext = createContext<AuthContextType | null>(null);

// Exportar el contexto para uso directo si es necesario
export { AuthContext };

/**
 * Proveedor de contexto de autenticación
 * Migrado para usar Zustand en lugar de useState
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({
    children,
    msalInstance,
    apiBaseUrl = '',
    defaultScopes = ['User.Read']
}) => {
    // Usar Zustand store en lugar de useState local
    const authService = useAuthStore((state) => state.authService as SecureAuthService | null);
    const apiService = useAuthStore((state) => state.apiService as SecureApiService | null);
    const isInitialized = useAuthStore((state) => state.isInitialized);
    const setAuthService = useAuthStore((state) => state.setAuthService);
    const setApiService = useAuthStore((state) => state.setApiService);
    const setInitialized = useAuthStore((state) => state.setInitialized);

    // Memoizar defaultScopes para evitar re-renders
    const memoizedDefaultScopes = React.useMemo(() => defaultScopes, [defaultScopes]);

    // Ref para mantener la referencia del unsubscribe
    const unsubscribeRef = useRef<(() => void) | null>(null);

    // Memoizar el contextValue ANTES del return condicional
    const contextValue: AuthContextType = React.useMemo(() => ({
        authService: authService!,
        apiService: apiService!,
        isInitialized
    }), [authService, apiService, isInitialized]);

    useEffect(() => {
        const initializeServices = async () => {
            try {
                // Esperar a que MSAL esté completamente inicializado
                await msalInstance.initialize();

                // Crear servicio de autenticación seguro
                const secureAuthService = new SecureAuthService(msalInstance);
                
                const syncAuthState = (authState: any) => {
                    const setAuthenticated = useAuthStore.getState().setAuthenticated;
                    const setAccount = useAuthStore.getState().setAccount;
                    const setLoading = useAuthStore.getState().setLoading;
                    const setError = useAuthStore.getState().setError;
                    
                    const activeAccount = msalInstance.getActiveAccount();
                    const hasActiveAccount = activeAccount !== null;
                    
                    const shouldBeAuthenticated = authState.isAuthenticated || hasActiveAccount;
                    
                    setAuthenticated(shouldBeAuthenticated);
                    setAccount(authState.account);
                    setLoading(authState.isLoading);
                    setError(authState.error);
                };
                
                // Suscribirse a cambios en el estado de autenticación
                const unsubscribe = secureAuthService.onAuthStateChanged(syncAuthState);
                unsubscribeRef.current = unsubscribe;
                
                // Inicializar el servicio de autenticación de forma asíncrona
                await secureAuthService.initialize();
                
                // Sincronizar el estado inicial después de la inicialización
                syncAuthState(secureAuthService.getAuthState());
                
                setAuthService(secureAuthService);

                // Crear servicio API seguro configurado para usar ID token
                const secureApiService = new SecureApiService(
                    secureAuthService,
                    apiBaseUrl,
                    memoizedDefaultScopes,
                    true // Usar ID token por defecto
                );
                setApiService(secureApiService);

                setInitialized(true);
            } catch (error) {
                console.error('Failed to initialize auth services:', error);
                setInitialized(true); // Permitir que la app continúe aunque falle la inicialización
            }
        };

        initializeServices();

        // Cleanup: desuscribirse cuando el componente se desmonte
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [msalInstance, apiBaseUrl, memoizedDefaultScopes, setAuthService, setApiService, setInitialized]);

    // No renderizar hasta que los servicios estén inicializados
    if (!isInitialized || !authService || !apiService) {
        return (
            <div className="auth-loading">
                <div>Initializing authentication...</div>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * Hook para usar el contexto de autenticación
 * @deprecated Use useAuth from hooks/auth directly for better performance
 */
export const useAuthContext = (): AuthContextType => {
    const context = useContext(AuthContext);
    
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    
    return context;
};

/**
 * Hook para usar solo el servicio de autenticación
 * @deprecated Use Zustand store directly
 */
export const useAuthService = (): SecureAuthService => {
    const { authService } = useAuthContext();
    return authService;
};

/**
 * Hook para usar solo el servicio API
 * @deprecated Use Zustand store directly
 */
export const useApiService = (): SecureApiService => {
    const { apiService } = useAuthContext();
    return apiService;
};

/**
 * HOC para componentes que requieren autenticación
 */
export const withAuth = <P extends object>(
    Component: React.ComponentType<P>
): React.FC<P> => {
    return (props: P) => {
        const { authService } = useAuthContext();
        const authState = authService.getAuthState();

        if (!authState.isAuthenticated) {
            return (
                <div className="auth-required">
                    <h2>Authentication Required</h2>
                    <p>Please sign in to access this content.</p>
                    <button onClick={() => authService.loginRedirect()}>
                        Sign In
                    </button>
                </div>
            );
        }

        return <Component {...props} />;
    };
};

/**
 * Componente de protección de rutas
 */
interface ProtectedRouteProps {
    children: ReactNode;
    fallback?: ReactNode;
    requireAuth?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    fallback,
    requireAuth = true
}) => {
    const { authService } = useAuthContext();
    const authState = authService.getAuthState();

    if (requireAuth && !authState.isAuthenticated) {
        if (fallback) {
            return <>{fallback}</>;
        }

        return (
            <div className="protected-route-fallback">
                <h2>Access Denied</h2>
                <p>You need to be authenticated to view this content.</p>
                <button onClick={() => authService.loginRedirect()}>
                    Sign In
                </button>
            </div>
        );
    }

    return <>{children}</>;
};

/**
 * Componente para mostrar información de depuración de autenticación
 * Migrado para usar Zustand en lugar de useState
 */
export const AuthDebugInfo: React.FC = () => {
    const { authService } = useAuthContext();
    const authState = useAuthStore((state) => ({
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        account: state.account,
        error: state.error,
    }));

    const debugInfo = React.useMemo(() => {
        const tokenInfo = authService?.getTokenCacheInfo();
        
        return {
            authState,
            tokenInfo,
            timestamp: new Date().toISOString()
        };
    }, [authService, authState]);

    if (!debugInfo) return null;

    return (
        <div style={{ 
            position: 'fixed', 
            bottom: 10, 
            right: 10, 
            background: '#f0f0f0', 
            padding: 10, 
            border: '1px solid #ccc',
            fontSize: '12px',
            maxWidth: '300px',
            zIndex: 9999
        }}>
            <h4>Auth Debug Info</h4>
            <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
        </div>
    );
};